// AdminDashboard.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useSocket } from '../../context/SocketContext';
import { format } from 'date-fns';
import { Users, CheckCircle, Clock, XCircle, Download, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

export default function AdminDashboard() {
  const socket = useSocket();
  const [stats, setStats] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, recordsRes] = await Promise.all([
        api.get('/attendance/company/stats'),
        api.get('/attendance/company/today')
      ]);
      setStats(statsRes.data);
      setRecords(recordsRes.data);
    } catch (err) {
      toast.error('Erreur chargement données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    if (!socket) return;
    socket.on('attendance_update', fetchData);
    return () => socket.off('attendance_update');
  }, [socket]);

  if (loading) return <div className="flex items-center justify-center h-64"><Clock className="w-8 h-8 text-blue-500 animate-spin" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Vue d'ensemble - {format(new Date(), 'dd/MM/yyyy')}</h1>
        <button onClick={fetchData} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium">
          <RefreshCw className="w-4 h-4" /> Actualiser
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total employés', value: stats.total, icon: Users, color: 'blue' },
            { label: 'Présents', value: stats.present, icon: CheckCircle, color: 'green' },
            { label: 'En retard', value: stats.late, icon: Clock, color: 'yellow' },
            { label: 'Absents', value: stats.total - stats.present, icon: XCircle, color: 'red' }
          ].map(item => (
            <div key={item.label} className={`bg-${item.color}-50 rounded-2xl p-5 border border-${item.color}-100`}>
              <item.icon className={`w-6 h-6 text-${item.color}-500 mb-3`} />
              <p className="text-3xl font-bold text-gray-800">{item.value}</p>
              <p className="text-gray-500 text-sm mt-0.5">{item.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Pointages du jour</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Employé', 'Entrée', 'Sortie', 'Durée', 'Statut', 'GPS'].map(h => (
                  <th key={h} className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {records.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-400">Aucun pointage aujourd'hui</td></tr>
              ) : records.map(rec => (
                <tr key={rec.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {rec.user?.avatar
                        ? <img src={rec.user.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                        : <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                            {rec.user?.firstName?.[0]}{rec.user?.lastName?.[0]}
                          </div>
                      }
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{rec.user?.firstName} {rec.user?.lastName}</p>
                        <p className="text-gray-400 text-xs">{rec.user?.department}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {rec.clockIn ? format(new Date(rec.clockIn), 'HH:mm') : '-'}
                    {rec.isLate && <span className="ml-1 text-orange-500 text-xs">+{rec.lateMinutes}min</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {rec.clockOut ? <>{format(new Date(rec.clockOut), 'HH:mm')}{(rec.clockOutNote || '').startsWith('[SORTIE HORS ZONE') && <span className="ml-1 text-xs font-bold text-red-500" title={rec.clockOutNote}>HZ</span>}</> : rec.clockIn ? <span className="text-green-500 text-xs">En cours</span> : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {rec.totalMinutes ? `${(rec.totalMinutes / 60).toFixed(1)}h` : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      rec.status === 'present' ? 'bg-green-100 text-green-700' :
                      rec.status === 'late' ? 'bg-yellow-100 text-yellow-700' :
                      rec.status === 'absent' ? 'bg-red-100 text-red-700' :
                      rec.status === 'on_leave' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {rec.status === 'present' ? 'Présent' : rec.status === 'late' ? 'En retard' : rec.status === 'absent' ? 'Absent' : rec.status === 'on_leave' ? 'En congé' : rec.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {rec.isWithinGeofence !== null && (
                      <span className={`text-xs ${rec.isWithinGeofence ? 'text-green-600' : 'text-red-500'}`}>
                        {rec.isWithinGeofence ? '✅ Zone' : `❌ ${rec.distanceFromOffice}m`}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
